import React from "react";
import {
  Box,
  Chip,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Stack,
  Pagination,
  Tooltip,
  alpha,
} from "@mui/material";
import {
  PersonOff as InactiveIcon,
  CheckCircle as ActiveIcon,
  Edit as EditIcon,
  Storage as LocalIcon,
} from "@mui/icons-material";

import { formatShortDate } from "../../utils/formatters";

// Helper to get workflow status display
const getWorkflowStatusChip = (status, isLocal = false) => {
  const statusMap = {
    Pending: { color: "warning", label: "Pending" },
    PENDING: { color: "warning", label: "Pending" },
    OPEN: { color: "info", label: "Open" },
    "In Progress": { color: "info", label: "In Progress" },
    Completed: { color: "success", label: "Completed" },
    Complete: { color: "success", label: "Complete" },
    Rejected: { color: "error", label: "Rejected" },
    "Charge Slip Completed": { color: "success", label: "Charge Slip Completed" },
    "Not addressed": { color: "default", label: "Not Addressed" },
    "Notes not signed by Doctor": { color: "warning", label: "Notes Not Signed" },
    "Charge Slip not Completed": { color: "warning", label: "Charge Slip Not Completed" },
    "Notes Signed by Doctor": { color: "success", label: "Notes Signed" },
  };
  const config = statusMap[status] || { color: "default", label: status || "Unknown" };
  return (
    <Chip
      icon={isLocal ? <LocalIcon sx={{ fontSize: 14 }} /> : undefined}
      label={config.label}
      color={config.color}
      size="small"
      variant={isLocal ? "filled" : "outlined"}
      sx={{ fontWeight: 500, fontSize: "0.7rem" }}
    />
  );
};

export default function PatientsTable({
  rows,
  loading,
  total,
  page,
  pageSize,
  onPageChange,
  onPageSizeChange,
  onOpenPatient,
}) {
  const totalPages = Math.max(1, Math.ceil((total || 0) / (pageSize || 1)));

  return (
    <Box>
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: "grey.50" }}>
              <TableCell sx={{ fontWeight: 700, py: 1.5 }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 700, py: 1.5 }}>Patient</TableCell>
              <TableCell sx={{ fontWeight: 700, py: 1.5 }}>Gender</TableCell>
              <TableCell sx={{ fontWeight: 700, py: 1.5 }}>DOB</TableCell>
              <TableCell sx={{ fontWeight: 700, py: 1.5 }}>Network / Plan</TableCell>
              <TableCell sx={{ fontWeight: 700, py: 1.5 }}>PCP</TableCell>
              <TableCell sx={{ fontWeight: 700, py: 1.5 }}>Medical Center</TableCell>
              <TableCell sx={{ fontWeight: 700, py: 1.5 }}>Workflow</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700, py: 1.5 }}># Ops</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={9}>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ py: 2 }}>
                    <CircularProgress size={18} />
                    <Typography variant="body2">Loading patients...</Typography>
                  </Stack>
                </TableCell>
              </TableRow>
            ) : null}

            {!loading && (!rows || rows.length === 0) ? (
              <TableRow>
                <TableCell colSpan={9}>
                  <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                    No patients found.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : null}

            {!loading &&
              (rows || []).map((r) => {
                // patientStatus: 1 = inactive, 0 = active
                const isInactive = r.patientStatus === 1;

                return (
                  <TableRow
                    key={r.id}
                    hover
                    sx={{
                      cursor: "pointer",
                      ...(isInactive && {
                        bgcolor: (theme) => alpha(theme.palette.error.light, 0.08),
                        "&:hover": {
                          bgcolor: (theme) => alpha(theme.palette.error.light, 0.15),
                        },
                      }),
                    }}
                    onClick={() => onOpenPatient?.(r)}
                  >
                    {/* Status Column */}
                    <TableCell>
                      {isInactive ? (
                        <Tooltip title="Inactive Patient" arrow>
                          <Chip
                            icon={<InactiveIcon sx={{ fontSize: 16 }} />}
                            label="Inactive"
                            size="small"
                            color="error"
                            variant="filled"
                            sx={{ fontWeight: 600, fontSize: "0.7rem" }}
                          />
                        </Tooltip>
                      ) : (
                        <Tooltip title="Active Patient" arrow>
                          <Chip
                            icon={<ActiveIcon sx={{ fontSize: 16 }} />}
                            label="Active"
                            size="small"
                            color="success"
                            variant="outlined"
                            sx={{ fontWeight: 500, fontSize: "0.7rem" }}
                          />
                        </Tooltip>
                      )}
                    </TableCell>

                    {/* Patient Name */}
                    <TableCell>
                      <Typography
                        variant="body2"
                        fontWeight={700}
                        sx={{ color: isInactive ? "error.dark" : "text.primary" }}
                      >
                        {r.memberFullName || "Unknown"}
                      </Typography>
                    </TableCell>

                    {/* Gender */}
                    <TableCell>
                      <Chip
                        label={r.gender || "—"}
                        size="small"
                        variant="outlined"
                        sx={{
                          minWidth: 32,
                          fontWeight: 500,
                          bgcolor: r.gender === "F" ? "pink.50" : r.gender === "M" ? "blue.50" : "grey.100",
                        }}
                      />
                    </TableCell>

                    {/* DOB */}
                    <TableCell>{formatShortDate(r.dateOfBirth)}</TableCell>

                    {/* Network / Plan */}
                    <TableCell>
                      <Stack spacing={0.5}>
                        <Typography variant="body2" fontWeight={500} noWrap sx={{ maxWidth: 200 }}>
                          {r.networkName || "—"}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: 200 }}>
                          {r.memberPlan || ""}
                          {r.metalLevel ? ` (${r.metalLevel})` : ""}
                        </Typography>
                      </Stack>
                    </TableCell>

                    {/* PCP */}
                    <TableCell>
                      <Typography variant="body2" noWrap sx={{ maxWidth: 180 }}>
                        {r.pcpName || "—"}
                      </Typography>
                      {r.pcpId ? (
                        <Typography variant="caption" color="text.secondary">
                          ID: {r.pcpId}
                        </Typography>
                      ) : null}
                    </TableCell>

                    {/* Medical Center */}
                    <TableCell>
                      <Typography variant="body2">{r.medicalCenter || "—"}</Typography>
                    </TableCell>

                    {/* Workflow Status */}
                    <TableCell>
                      <Stack spacing={0.5} alignItems="flex-start">
                        {getWorkflowStatusChip(r.patientWorkflowStatus, !!r.localPatientStatus)}
                        {r.localPatientStatus && (
                          <Tooltip title={`Updated by ${r.localPatientStatus.completedBy} on ${formatShortDate(r.localPatientStatus.completedAt)}`}>
                            <Typography variant="caption" color="secondary.main" sx={{ fontSize: "0.65rem" }}>
                              Local
                            </Typography>
                          </Tooltip>
                        )}
                      </Stack>
                    </TableCell>

                    {/* Coding Ops Count */}
                    <TableCell align="right">
                      <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="flex-end">
                        {r.localOverridesCount > 0 && (
                          <Tooltip title={`${r.localOverridesCount} coding opportunity decision(s) modified`}>
                            <Chip
                              icon={<EditIcon sx={{ fontSize: 14 }} />}
                              label={r.localOverridesCount}
                              size="small"
                              color="secondary"
                              variant="outlined"
                              sx={{ fontWeight: 600, fontSize: "0.7rem", minWidth: 32 }}
                              onClick={(e) => e.stopPropagation()}
                            />
                          </Tooltip>
                        )}
                        <Chip
                          label={r.codingOpCount ?? 0}
                          size="small"
                          color="primary"
                          sx={{ fontWeight: 700, minWidth: 40 }}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}
          </TableBody>
        </Table>
      </TableContainer>

      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={2}
        alignItems="center"
        justifyContent="space-between"
        sx={{ mt: 2 }}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="body2" color="text.secondary">
            Page size:
          </Typography>
          <Chip
            label={pageSize}
            onClick={() => onPageSizeChange?.(15)}
            variant={pageSize === 15 ? "filled" : "outlined"}
            size="small"
          />
          <Chip
            label={25}
            onClick={() => onPageSizeChange?.(25)}
            variant={pageSize === 25 ? "filled" : "outlined"}
            size="small"
          />
          <Chip
            label={50}
            onClick={() => onPageSizeChange?.(50)}
            variant={pageSize === 50 ? "filled" : "outlined"}
            size="small"
          />
        </Stack>

        <Pagination
          count={totalPages}
          page={page || 1}
          onChange={(_, p) => onPageChange?.(p)}
          color="primary"
        />
      </Stack>
    </Box>
  );
}
